import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LOCALES, type LocaleCode } from '../config/i18n';

/** Ülke bayrakları (emoji) – dil kodu -> bayrak */
const LOCALE_FLAGS: Record<LocaleCode, string> = {
  tr: '🇹🇷',
  en: '🇬🇧',
};

interface LanguageSwitchProps {
  /** Compact: sadece bayrak. Full: "Dil" etiketi + bayrak */
  variant?: 'compact' | 'full';
  className?: string;
}

export default function LanguageSwitch({ variant = 'full', className = '' }: LanguageSwitchProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const current = (i18n.language?.split('-')[0] || 'en') as LocaleCode;
  const effective = SUPPORTED_LOCALES.includes(current) ? current : 'en';

  const setLanguage = (code: LocaleCode) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 px-2 py-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm transition-colors"
        aria-label={t('language.label')}
        aria-expanded={open}
        aria-haspopup="listbox"
        title={t(`language.${effective}`)}
      >
        <span className="text-lg leading-none" role="img" aria-hidden>
          {LOCALE_FLAGS[effective]}
        </span>
        <span className="font-medium uppercase text-xs">{effective}</span>
        {variant === 'full' && <span>{t('language.label')}</span>}
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute right-0 mt-1 py-1 w-44 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg z-50"
        >
          {SUPPORTED_LOCALES.map((code) => (
            <li key={code} role="option" aria-selected={effective === code}>
              <button
                type="button"
                onClick={() => setLanguage(code)}
                className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                  effective === code ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-medium' : ''
                }`}
              >
                <span className="text-lg leading-none" role="img" aria-hidden>
                  {LOCALE_FLAGS[code]}
                </span>
                {t(`language.${code}`)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
